// The search box (docs/SPEC.md item 6; web/main.js search wiring): typing waits for a short pause before the map and
// list follow, so each key stays instant; Enter / the list button puts the keyboard away and shows the results in
// the list; Escape / ✕ clear it and leave search. My events and Profile sit at its end (the profile's slot).
import { useEffect, useRef, useState } from "react";
import { T } from "../../shared/config";
import { Icon } from "../../shared/icons";
import { closeSheet, setNearbyExpanded, setSearch, useView } from "../../state/view";
import { IconButton } from "../../ui/IconButton";
import { ProfileButtons } from "../profile/slots";
import styles from "./SearchBox.module.css";

const PAUSE = 150;

export function SearchBox() {
  const committed = useView((s) => s.filter.search);
  const [text, setText] = useState(committed);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const input = useRef<HTMLInputElement>(null);
  // Cleared elsewhere (or restored from the URL): the box follows.
  useEffect(() => { if (!timer.current) setText(committed); }, [committed]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const flush = (v = text) => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } setSearch(v); };
  const onChange = (v: string) => {
    setText(v);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { timer.current = null; setSearch(v); }, PAUSE);
  };
  const showResults = () => { flush(); input.current?.blur(); closeSheet(); setNearbyExpanded(true); };
  const clear = () => { setText(""); flush(""); input.current?.blur(); setNearbyExpanded(false); };
  const searching = committed.trim() !== "";

  return (
    <div className={styles.search}>
      <Icon name="search" className={styles.icon} />
      <input
        ref={input} id="search" type="text" inputMode="search" enterKeyHint="search" autoComplete="off" spellCheck={false}
        placeholder={T("search.placeholder")} aria-label={T("search.placeholder")} value={text}
        onChange={(ev) => onChange(ev.target.value)}
        onKeyDown={(ev) => {
          if (ev.key === "Enter") { ev.preventDefault(); showResults(); }
          else if (ev.key === "Escape") { ev.stopPropagation(); clear(); }
        }}
      />
      {searching && <IconButton id="show-results" icon="list" label={T("search.showResults")} onClick={showResults} />}
      {searching && <IconButton id="clear-search" icon="x" label={T("search.clear")} onClick={clear} />}
      <ProfileButtons />
    </div>
  );
}
